/**
 * Background Update Manager
 * Manages background cache updates with intelligent scheduling
 */

import pageCacheManager from './pageCacheManager.js';

class BackgroundUpdateManager {
  constructor() {
    this.isInitialized = false;
    this.userActivityTimer = null;
    this._onDomainDataChange = null;
    this.performanceMonitor = {
      lastUserActivity: Date.now(),
      isUserActive: false,
      cpuUsage: 0,
      memoryUsage: 0
    };
  }

  /**
   * Initialize background update manager
   */
  initialize() {
    if (this.isInitialized) return;

    this.setupUserActivityMonitoring();
    this.setupDataChangeListener();
    this.setupPerformanceMonitoring();
    this.isInitialized = true;

    // Background update manager initialized
  }

  /**
   * Setup user activity monitoring
   */
  setupUserActivityMonitoring() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    const resetUserActivityTimer = () => {
      this.performanceMonitor.lastUserActivity = Date.now();
      this.performanceMonitor.isUserActive = true;
      
      // Pause background work during user activity
      pageCacheManager.pause();
      
      // Clear existing timer
      if (this.userActivityTimer) {
        clearTimeout(this.userActivityTimer);
      }
      
      // Set timer to resume background work after inactivity
      this.userActivityTimer = setTimeout(() => {
        this.performanceMonitor.isUserActive = false;
        pageCacheManager.resume();
      }, 2000); // Resume after 2 seconds of inactivity
    };

    events.forEach(event => {
      document.addEventListener(event, resetUserActivityTimer, { passive: true });
    });
  }

  /**
   * Setup data change listener
   */
  setupDataChangeListener() {
    this._onDomainDataChange = (event) => {
      if (event?.detail?.type === 'data_changed') {
        this.handleDataChange(event.detail?.payload || {});
      }
    };

    window.addEventListener('elkheir:domain-event', this._onDomainDataChange);
  }

  /**
   * Handle data change events
   */
  handleDataChange(changeEvent) {
    const { dataType, affectedPages, businessId } = changeEvent;
    
    // Ensure affectedPages is an array
    const pagesToUpdate = Array.isArray(affectedPages) ? affectedPages : ['inventory', 'sales', 'purchases', 'dashboard'];
    
    // Queue background updates for affected pages
    pagesToUpdate.forEach(pageName => {
      if (pageCacheManager && typeof pageCacheManager.queueBackgroundUpdate === 'function') {
        pageCacheManager.queueBackgroundUpdate(pageName, {
          ...changeEvent,
          businessId
        });
      }
    });
  }

  /**
   * Setup performance monitoring
   */
  setupPerformanceMonitoring() {
    const METRICS_MS = 30_000;

    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      this.updatePerformanceMetrics();
    };

    tick();
    this._metricsInterval = setInterval(tick, METRICS_MS);

    if (typeof document !== 'undefined') {
      this._onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          tick();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibilityChange);
    }
  }

  /**
   * Update performance metrics
   */
  updatePerformanceMetrics() {
    // Simple performance monitoring
    const now = Date.now();
    const timeSinceActivity = now - this.performanceMonitor.lastUserActivity;
    
    // Estimate CPU usage based on activity
    const cpuUsage = this.performanceMonitor.isUserActive ? 50 : 10;
    
    // Estimate memory usage (simplified)
    const memoryUsage = (performance.memory?.usedJSHeapSize / performance.memory?.totalJSHeapSize) * 100 || 30;
    
    // Update performance monitor
    this.performanceMonitor.cpuUsage = cpuUsage;
    this.performanceMonitor.memoryUsage = memoryUsage;
    
    // Update page cache manager
    pageCacheManager.updatePerformanceMetrics(
      cpuUsage,
      memoryUsage,
      this.performanceMonitor.isUserActive
    );
  }

  /**
   * Register page for background caching
   */
  registerPage(pageName, dependencies, calculationFn, priority = 'medium') {
    pageCacheManager.registerPage(pageName, dependencies, calculationFn, priority);
  }

  /**
   * Get page cache
   */
  getPageCache(pageName, businessId) {
    return pageCacheManager.getPageCache(pageName, businessId);
  }

  /**
   * Set page cache
   */
  setPageCache(pageName, businessId, data) {
    return pageCacheManager.setPageCache(pageName, businessId, data);
  }

  /**
   * Get cache statistics
   */
  getCacheStatistics() {
    return pageCacheManager.getCacheStatistics();
  }

  /**
   * Pause background processing
   */
  pause() {
    pageCacheManager.pause();
  }

  /**
   * Resume background processing
   */
  resume() {
    pageCacheManager.resume();
  }

  /**
   * Clear all caches
   */
  clearAllCaches() {
    pageCacheManager.clearAllPageCaches();
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics() {
    return {
      ...this.performanceMonitor,
      isInitialized: this.isInitialized
    };
  }

  /**
   * Cleanup
   */
  cleanup() {
    if (this.userActivityTimer) {
      clearTimeout(this.userActivityTimer);
    }

    if (this._metricsInterval) {
      clearInterval(this._metricsInterval);
      this._metricsInterval = null;
    }

    if (this._onVisibilityChange && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      this._onVisibilityChange = null;
    }
    
    if (this._onDomainDataChange) {
      window.removeEventListener('elkheir:domain-event', this._onDomainDataChange);
    }
    this.isInitialized = false;
  }
}

// Create singleton instance
const backgroundUpdateManager = new BackgroundUpdateManager();

export default backgroundUpdateManager;
